import React, { createContext, useContext, useState, useMemo } from 'react';

const ThemeContext = createContext(null);

const FONTS_JADE = { display: "'Bricolage Grotesque', system-ui, sans-serif", body: "'Source Serif 4', Georgia, serif", data: "'JetBrains Mono', monospace" };
const STYLES_JADE = { headingWeight: 600, headingTracking: '-0.025em', bodyWeight: 300, bodyLineHeight: 1.75, logoWeight: 600, logoTracking: '-0.025em' };

// ─── JADE — Forest, Parchment & Old Gold ─────────────────────────────────────
// Brand system: Forest (#0A1812) = authority/chrome, Parchment (#F4F0E4) =
// the editorial canvas, Old Gold (#C49A38) = a restrained signal accent.
// Dark mode (the authenticated app) uses a 4-level surface hierarchy —
// bgDeep (page) < bg (Forest chrome/nav) < surface (card) < surfaceHi (nested)
// — so cards read as distinct layers instead of one flat green wash.
const JADE = {
  name: 'jade', fonts: FONTS_JADE, styles: STYLES_JADE,
  dark: {
    nerveRgb: '196,154,56', bgRgb: '10,24,18', bgDeepRgb: '7,16,13', surfaceRgb: '14,26,21',
    colors: {
      bgDeep: '#07100D', bg: '#0A1812', surface: '#0E1A15', surfaceHi: '#152019',
      border: 'rgba(196,154,56,0.10)', borderHi: 'rgba(196,154,56,0.35)',
      text: '#F0EBD8', textMuted: 'rgba(240,235,216,0.60)', textDim: '#C49A38',
      action: '#C49A38', actionHi: '#D4AF5A', actionLo: '#9A7820',
      goldDim: 'rgba(196,154,56,0.18)', goldFaint: 'rgba(196,154,56,0.08)',
      bull: '#5DB860', bear: '#D4563A', inputBg: '#0E1A15',
    },
  },
  light: {
    nerveRgb: '154,120,32', bgRgb: '244,240,228', bgDeepRgb: '244,240,228', surfaceRgb: '255,255,253',
    colors: {
      bgDeep: '#F4F0E4', bg: '#F4F0E4', surface: '#FFFFFD', surfaceHi: '#EDE7D8',
      border: 'rgba(10,24,18,0.10)', borderHi: 'rgba(10,24,18,0.20)',
      text: '#0A1812', textMuted: 'rgba(10,24,18,0.60)', textDim: '#9A7820',
      action: '#9A7820', actionHi: '#C49A38', actionLo: '#7A5A10',
      goldDim: 'rgba(196,154,56,0.18)', goldFaint: 'rgba(196,154,56,0.10)',
      bull: '#2E7D32', bear: '#C62828', inputBg: '#EDE7D8',
    },
  },
};

const PALETTES = { jade: JADE };
export const PALETTE_ORDER = ['jade'];
export const PALETTE_ACCENTS = { jade: JADE.dark.colors.action };
export const JADE_DARK = JADE.dark;
export const JADE_LIGHT = JADE.light;
export const JADE_FONTS = FONTS_JADE;

// ─── PROVIDER ─────────────────────────────────────────────────────────────────

export function ThemeProvider({ children }) {
  // The authenticated app defaults to dark (Midnight Jade); public marketing
  // pages don't read this toggle at all — they pin their own light palette
  // via JADE_LIGHT so they're never affected by whatever the app was left in.
  const [mode, setMode] = useState(() => localStorage.getItem('mn_mode') || 'dark');

  const toggleMode = () => {
    setMode(prev => {
      const next = prev === 'dark' ? 'light' : 'dark';
      localStorage.setItem('mn_mode', next);
      return next;
    });
  };

  // Kept as stubs — palette is fixed to jade
  const togglePalette = () => {};
  const setPalette = () => {};

  const value = useMemo(() => {
    const pal = PALETTES.jade;
    const modeData = pal[mode];
    return {
      palette: 'jade',
      mode,
      toggleMode,
      togglePalette,
      setPalette,
      colors:     modeData.colors,
      fonts:      pal.fonts,
      styles:     pal.styles,
      nerveRgb:   modeData.nerveRgb,
      bgRgb:      modeData.bgRgb,
      bgDeepRgb:  modeData.bgDeepRgb,
      surfaceRgb: modeData.surfaceRgb,
    };
  }, [mode]);

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  return useContext(ThemeContext);
}
